import * as React from "react";
import { EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";

interface Props {
  columns: string[];
  hiddenColumns: string[];
  onHiddenColumnsChange: (hiddenColumns: string[]) => void;
}

export const ColumnVisibilityDropdown: React.FC<Props> = ({ columns, hiddenColumns, onHiddenColumnsChange }) => {
  const hasHidden = hiddenColumns.length > 0;

  const toggleColumn = (column: string) => {
    if (hiddenColumns.includes(column)) {
      onHiddenColumnsChange(hiddenColumns.filter(c => c !== column));
    } else {
      onHiddenColumnsChange([...hiddenColumns, column]);
    }
  };
  // A note column with 1+ notes produces duplicate column names; dedupe before render.
  const columnsToDisplay = React.useMemo(() => [...new Set(columns)], [columns]);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant={hasHidden ? "default" : "outline"} size="sm" className="gap-2">
          <EyeOff className="h-4 w-4" />
          Columns
          {hasHidden && (
            <span className="ml-1 rounded-full bg-primary-foreground px-1.5 py-0.5 text-xs text-primary">
              {hiddenColumns.length}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 p-2">
        <p className="mb-2 px-2 text-xs font-semibold uppercase text-gray-500">Hide columns</p>
        <div className="flex max-h-[25rem] flex-col gap-1 overflow-y-scroll">
          {columnsToDisplay.map(column => (
            <label
              key={column}
              className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-gray-100"
            >
              <Checkbox checked={hiddenColumns.includes(column)} onCheckedChange={() => toggleColumn(column)} />
              <span className="truncate">{column}</span>
            </label>
          ))}
          {columns.length === 0 && <p className="px-2 py-1 text-sm text-gray-400">No columns available</p>}
        </div>
        {hasHidden && (
          <div className="mt-2 border-t pt-2">
            <button
              className="w-full rounded px-2 py-1 text-left text-xs text-gray-500 hover:bg-gray-100"
              onClick={() => onHiddenColumnsChange([])}
            >
              Show all columns
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
